// babyworld-backoffice\src\pages\dashboard\DashboardPage.js

import React, { useState, useEffect, useCallback } from 'react';
import { 
    Row, Col, Card, Typography, Select, Button, Table, Badge, Tag, 
    Spin, message, DatePicker, Space, Empty, List, Tooltip, Flex, 
    Statistic, Avatar, Input, Modal, Collapse
} from 'antd';
import { 
    TeamOutlined, CheckCircleOutlined, SyncOutlined, 
    AlertOutlined, UserAddOutlined, DownloadOutlined,
    CalendarOutlined, UserOutlined, EnvironmentOutlined,
    GlobalOutlined, InfoCircleOutlined, MessageOutlined,
    ReloadOutlined, SearchOutlined,
    AuditOutlined, StarFilled, RocketOutlined, HistoryOutlined, 
    WarningOutlined, CompassOutlined, MedicineBoxOutlined, ClockCircleOutlined, CrownOutlined 
} from '@ant-design/icons';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, 
    Tooltip as ChartTooltip, ResponsiveContainer, Legend, 
    PieChart, Pie, Cell, ReferenceLine 
} from 'recharts';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// --- DÉCLARATION HORS DU COMPOSANT ---
//const baseUrl = 'https://preprod-admin.babyworld.tn/api/v1';
const baseUrl = 'https://api.admin.babyworld.tn/api/v1';
const getConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});
const PIE_COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96'];

// --- COMPOSANT TOOLTIP PERSONNALISÉ (BARRES) ---
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;

        return (
            <div style={{ 
                backgroundColor: '#fff', 
                border: '1px solid #d9d9d9', 
                padding: '12px 14px', 
                borderRadius: '8px', 
                boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                width: '320px',
                pointerEvents: 'none'
            }}>
                <p style={{ fontWeight: 'bold', fontSize: '13px', margin: '0 0 6px 0', color: '#1f1f1f' }}>
                    {data.fullDate || label}
                </p>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                        Total: {data.visites || data.realized} visites
                    </span>
                    {data.planned !== undefined && (
                        <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                            🎯 Obj: {data.planned}
                        </span>
                    )}
                </div>

                {data.regions && data.regions.length > 0 && (
                    <div style={{ marginTop: '8px', borderTop: '1px solid #f0f0f0', paddingTop: '8px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#ff4d4f', marginBottom: '6px' }}>
                            📍 RÉGIONS COUVERTES ({data.regions.length}) :
                        </div>

                        <div style={{ 
                            maxHeight: '280px',
                            overflowY: 'hidden',
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(2, 1fr)', 
                            gap: '4px 8px' 
                        }}>
                            {data.regions.map((r, i) => (
                                <div 
                                    key={i} 
                                    style={{ 
                                        backgroundColor: '#fafafa',
                                        borderRadius: '4px',
                                        padding: '2px 6px',
                                        fontSize: '10px',
                                        color: '#262626',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        border: '1px solid #f0f0f0'
                                    }}
                                    title={typeof r === 'string' ? r : r.name}
                                >
                                    • {typeof r === 'string' ? r : r.name}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }
    return null;
};

const DashboardPage = () => {
    const [loading, setLoading] = useState(false);
    const [kpis, setKpis] = useState(null);
    const [rankings, setRankings] = useState([]);
    const [dailyTrend, setDailyTrend] = useState([]);
    const [delegates, setDelegates] = useState([]);
    const [regionAnalysis, setRegionAnalysis] = useState({});
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [alerts, setAlerts] = useState([]); 
    const [regionAlerts, setRegionAlerts] = useState([]);
    const [regionSearch, setRegionSearch] = useState('');

    // --- ÉTATS MODALE HISTORIQUE MÉDECIN ---
    const [doctorModalVisible, setDoctorModalVisible] = useState(false);
    const [doctorDetailData, setDoctorDetailData] = useState([]);
    const [doctorLoading, setDoctorLoading] = useState(false);

    const [selectedBarDelegate, setSelectedBarDelegate] = useState(null);

    // --- ÉTATS MONITORING J-1 ET HISTORIQUE ---
    const [dailyMonitoring, setDailyMonitoring] = useState(null);
    const [loadingDaily, setLoadingDaily] = useState(false);

    const [historyModalVisible, setHistoryModalVisible] = useState(false);
    const [historyDate, setHistoryDate] = useState(dayjs().subtract(2, 'days'));
    const [historyData, setHistoryData] = useState(null);
    const [loadingHistory, setLoadingHistory] = useState(false);
    
    // Filtres généraux
    const [selectedDelegate, setSelectedDelegate] = useState(null);
    const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs()]);
    const [selectedCategory, setSelectedCategory] = useState('all');

    // --- ÉTATS SEMAINE PRÉCÉDENTE (MANQUÉS & HORS PORTEFEUILLE) ---
    const [prevWeekData, setPrevWeekData] = useState(null);
    const [loadingPrevWeek, setLoadingPrevWeek] = useState(false);

    // Modale de détail (médecins manqués ou non assignés)
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [modalDetails, setModalDetails] = useState({ title: '', type: '', list: [] });

    const [offPlanModalVisible, setOffPlanModalVisible] = useState(false);
    const [selectedOffPlanInfo, setSelectedOffPlanInfo] = useState({ region: '', delegateName: '', doctors: [] });

    const [prevWeekHistoryModalVisible, setPrevWeekHistoryModalVisible] = useState(false);
    const [historyWeekRange, setHistoryWeekRange] = useState([
        dayjs().subtract(2, 'week').day(1), // Lundi d'il y a 2 semaines
        dayjs().subtract(2, 'week').day(7)  // Dimanche
    ]);
    const [historyWeekData, setHistoryWeekData] = useState(null);
    const [loadingHistoryWeek, setLoadingHistoryWeek] = useState(false);

    // État pour afficher les détails des visites superviseurs
    const [supervisorModalVisible, setSupervisorModalVisible] = useState(false);
    const [selectedSupervisorVisits, setSelectedSupervisorVisits] = useState({ delegateName: '', visits: [] });

    const handleOpenSupervisorVisits = (delegateName, visits) => {
        setSelectedSupervisorVisits({ delegateName, visits: visits || [] });
        setSupervisorModalVisible(true);
    };

    // Fonction pour charger une semaine historique
    const fetchHistoryWeekCompliance = async (range) => {
        if (!range || !range[0] || !range[1]) return;
        setLoadingHistoryWeek(true);
        try {
            const startStr = range[0].format('YYYY-MM-DD');
            const endStr = range[1].format('YYYY-MM-DD');
            const catParam = selectedCategory && selectedCategory !== 'all' ? `&category=${selectedCategory}` : '';
            const delParam = selectedDelegate ? `&delegateId=${selectedDelegate}` : '';

            const res = await axios.get(
                `${baseUrl}/dashboard/previous-week-compliance?startDate=${startStr}&endDate=${endStr}${catParam}${delParam}`, 
                getConfig()
            );
            setHistoryWeekData(res.data);
        } catch (e) {
            message.error("Erreur lors du chargement de l'historique de la semaine.");
        } finally {
            setLoadingHistoryWeek(false);
        }
    };


    const [frequentVisitsAlerts, setFrequentVisitsAlerts] = useState([]);

    // 1. Charger les délégués au montage
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const res = await axios.get(`${baseUrl}/delegates?minimal=true`, getConfig());
                setDelegates(res.data || []);
            } catch (e) { 
                console.error("Erreur delegates:", e); 
            }
        };
        fetchFilters();
    }, []);

    // Charger le monitoring J-1
    const fetchDailyMonitoring = useCallback(async () => {
        setLoadingDaily(true);
        try {
            let params = new URLSearchParams();
            if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
            if (selectedDelegate) params.append('delegateId', selectedDelegate); // <--- TRANSMET LE DÉLÉGUÉ SÉLECTIONNÉ
            const query = params.toString() ? `?${params.toString()}` : '';

            const res = await axios.get(`${baseUrl}/dashboard/daily-monitoring${query}`, getConfig());
            setDailyMonitoring(res.data);
        } catch (e) {
            console.error("Erreur chargement monitoring J-1", e);
        } finally {
            setLoadingDaily(false);
        }
    }, [selectedCategory, selectedDelegate]);

    // Charger une date d'historique
    const fetchHistoryMonitoring = async (date) => {
        if (!date) return;
        setLoadingHistory(true);
        try {
            const catParam = selectedCategory && selectedCategory !== 'all' ? `&category=${selectedCategory}` : '';
            const delParam = selectedDelegate ? `&delegateId=${selectedDelegate}` : '';
            const res = await axios.get(
                `${baseUrl}/dashboard/daily-monitoring?date=${date.format('YYYY-MM-DD')}${catParam}${delParam}`, 
                getConfig()
            );
            setHistoryData(res.data);
        } catch (e) {
            message.error("Erreur lors de la récupération de l'historique.");
        } finally {
            setLoadingHistory(false);
        }
    };

    useEffect(() => {
        fetchDailyMonitoring();
    }, [fetchDailyMonitoring]);

    const fetchPrevWeekCompliance = useCallback(async () => {
        setLoadingPrevWeek(true);
        try {
            let params = new URLSearchParams();
            if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
            if (selectedDelegate) params.append('delegateId', selectedDelegate); // <--- TRANSMET LE DÉLÉGUÉ SÉLECTIONNÉ
            const query = params.toString() ? `?${params.toString()}` : '';

            const res = await axios.get(`${baseUrl}/dashboard/previous-week-compliance${query}`, getConfig());
            setPrevWeekData(res.data);
        } catch (e) {
            console.error("Erreur chargement conformité semaine précédente", e);
        } finally {
            setLoadingPrevWeek(false);
        }
    }, [selectedCategory, selectedDelegate]);

    useEffect(() => {
        fetchPrevWeekCompliance();
    }, [fetchPrevWeekCompliance]);

    const handleOpenDetails = (delegateName, type, list, currentCycleInfo = null) => {
        setModalDetails({
            title: type === 'missed' 
                ? `Médecins Manqués - ${delegateName}` 
                : `Médecins Hors Portefeuille Visités - ${delegateName}`,
            type,
            list,
            delegateName,
            currentCycleInfo // <--- ON ENREGISTRE LES INFOS DU CYCLE
        });
        setDetailModalVisible(true);
    };

    const prevWeekColumns = [
        {
            title: 'Délégué',
            dataIndex: 'delegateName',
            key: 'delegateName',
            width: 220,
            render: (name) => <Text strong><UserOutlined style={{ marginRight: 6 }} />{name}</Text>
        },
        {
            title: 'Visites Réalisées',
            dataIndex: 'totalVisits',
            key: 'totalVisits',
            align: 'center',
            width: 170,
            render: (count, record) => (
                <Space direction="vertical" size={3} align="center" style={{ width: '100%' }}>
                    <Tag color="blue" style={{ fontWeight: 'bold', margin: 0 }}>
                        {count} visite{count > 1 ? 's' : ''}
                    </Tag>

                    {/* Badge si le délégué était en congé */}
                    {record.leaves && record.leaves.length > 0 && (
                        record.leaves.map((l, idx) => (
                            <Tooltip key={idx} title={`Motif : ${l.reason} (du ${l.start} au ${l.end})`}>
                                <Tag color="warning" style={{ fontSize: '11px', fontWeight: 'bold', margin: 0, cursor: 'help' }}>
                                    🌴 Congé ({l.start} - {l.end})
                                </Tag>
                            </Tooltip>
                        ))
                    )}

                    {/* Badge si la semaine contenait un jour férié */}
                    {record.holidays && record.holidays.length > 0 && (
                        record.holidays.map((h, idx) => (
                            <Tooltip key={idx} title={`Jour férié : ${h.name} le ${h.date}`}>
                                <Tag color="purple" style={{ fontSize: '10px', margin: 0, cursor: 'help' }}>
                                    🎉 Férié : {h.name}
                                </Tag>
                            </Tooltip>
                        ))
                    )}
                </Space>
            )
        },
        {
            title: 'Visites Manquées (Semaine N-1)',
            dataIndex: 'missedCount',
            key: 'missedCount',
            align: 'center',
            width: 220,
            render: (count, record) => (
                count > 0 ? (
                    <Button 
                        danger 
                        size="small" 
                        onClick={() => handleOpenDetails(record.delegateName, 'missed', record.missedDoctors, record.currentCycleInfo)}
                    >
                        ⚠️ {count} médecin{count > 1 ? 's' : ''} manqué{count > 1 ? 's' : ''}
                    </Button>
                ) : (
                    <Tag color="success" icon={<CheckCircleOutlined />}>Aucun manqué</Tag>
                )
            )
        },
        {
            title: 'Médecins Hors Planning Semaine N-1',
            dataIndex: 'unassignedCount',
            key: 'unassignedCount',
            align: 'center',
            width: 250,
            render: (count, record) => (
                count > 0 ? (
                    <Button 
                        type="primary" 
                        ghost 
                        size="small" 
                        style={{ borderColor: '#fa8c16', color: '#fa8c16' }}
                        onClick={() => handleOpenDetails(record.delegateName, 'unassigned', record.unassignedDoctors, record.currentCycleInfo)}
                    >
                        ⭐ {count} hors planning visité{count > 1 ? 's' : ''}
                    </Button>
                ) : (
                    <Tag color="default" style={{ color: '#52c41a' }}>0 (100% Conforme au Planning)</Tag>
                )
            )
        },
        // --- NOUVELLE COLONNE : VISITES SUPERVISEURS (SEMAINE N-1) ---
        {
            title: 'Contrôle Superviseur',
            dataIndex: 'supervisorVisitsCount',
            key: 'supervisorVisitsCount',
            align: 'center',
            width: 190,
            render: (count, record) => (
                count > 0 ? (
                    <Button 
                        size="small" 
                        style={{ 
                            backgroundColor: '#f9f0ff', 
                            borderColor: '#d3adf7', 
                            color: '#722ed1',
                            fontWeight: 'bold' 
                        }}
                        onClick={() => handleOpenSupervisorVisits(record.delegateName, record.supervisorVisits)}
                    >
                        👑 {count} contrôle{count > 1 ? 's' : ''} / acc.
                    </Button>
                ) : (
                    <Text type="secondary" style={{ fontSize: '12px' }}>Aucun</Text>
                )
            )
        }
    ];

    // 2. Charger toutes les données du dashboard
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };

            let params = new URLSearchParams();
            if (selectedDelegate) params.append('delegateId', selectedDelegate);
            if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory); // <--- AJOUTÉ
            if (dateRange && dateRange[0]) {
                params.append('startDate', dateRange[0].format('YYYY-MM-DD'));
                params.append('endDate', dateRange[1].format('YYYY-MM-DD'));
            }
            const query = params.toString();

            const [
                resKpi, resRank, resTrend, resRegion, resAlerts, resRegionAlerts, resFrequentAlerts
            ] = await Promise.all([
                axios.get(`${baseUrl}/dashboard/kpis?${query}`, config),                
                axios.get(`${baseUrl}/dashboard/rankings?${query}`, config),             
                axios.get(`${baseUrl}/dashboard/daily-trend?${query}`, config),           
                axios.get(`${baseUrl}/dashboard/region-analysis?${query}`, config),       
                axios.get(`${baseUrl}/dashboard/alerts/missing-visits?${query}`, config), 
                axios.get(`${baseUrl}/dashboard/alerts/missing-regions?${query}`, config),
                axios.get(`${baseUrl}/dashboard/alerts/frequent-visits?${query}`, config)
            ]);
            
            setKpis(resKpi.data);
            setRankings(resRank.data);
            setDailyTrend(resTrend.data);
            setRegionAnalysis(resRegion.data || {});
            setAlerts(resAlerts.data || []);
            setRegionAlerts(resRegionAlerts.data || []); 
            setFrequentVisitsAlerts(resFrequentAlerts.data || []);

        } catch (error) { 
            console.error("Erreur chargement dashboard:", error);
        } finally { 
            setLoading(false); 
        }
    }, [selectedDelegate, dateRange, selectedCategory]);

    useEffect(() => { 
        loadData(); 
    }, [loadData]);

    // --- OUVERTURE DE L'HISTORIQUE MÉDECIN ---
    const handleOpenDoctorHistory = async (personId) => {
        setDoctorModalVisible(true);
        setDoctorLoading(true);
        try {
            const [resDetails, resPerson] = await Promise.all([
                axios.get(`${baseUrl}/delegate-stocks/analytics/doctor-full-details`, {
                    params: { 
                        personId, 
                        startDate: '2020-01-01', 
                        endDate: dayjs().format('YYYY-MM-DD'), 
                        _t: Date.now() 
                    },
                    ...getConfig()
                }),
                axios.get(`${baseUrl}/persons/${personId}`, getConfig()).catch(() => null)
            ]);

            const details = resDetails.data || [];
            const personData = resPerson?.data?.data || resPerson?.data;
            const partnerNotes = personData?.partnerNotes || details[0]?.partnerNotes;

            if (details.length > 0) {
                details[0].partnerNotes = partnerNotes;
            } else if (personData) {
                details.push({
                    doctorName: `Dr. ${personData.firstname || ''} ${personData.lastname || ''}`,
                    isPartner: personData.isPartner,
                    specialtyName: personData.specialty?.name || personData.doctor?.specialty?.name || "N/A",
                    regionName: personData.region?.name || "N/A",
                    assignedDelegates: personData.delegates?.map(d => `${d.firstname || ''} ${d.lastname || ''}`) || [],
                    partnerNotes: partnerNotes
                });
            }

            setDoctorDetailData(details);
        } catch (e) { 
            message.error("Erreur lors du chargement de l'historique du médecin"); 
        } finally { 
            setDoctorLoading(false); 
        }
    };

    // --- EXPORT CSV DE L'HISTORIQUE MÉDECIN ---
    const handleExportDoctorHistory = () => {
        if (doctorDetailData.length === 0) return;
        const docName = doctorDetailData[0].doctorName;
        const headers = ["Date", "Region", "Delegue Visiteur", "Echantillons", "Produits"];
        const rows = doctorDetailData.map(item => [
            dayjs(item.reportDate).format('DD/MM/YYYY HH:mm'),
            item.regionName || "N/A",
            item.delegateName,
            item.samples?.map(s => `${s.name} (x${s.quantity})`).join(' | '),
            item.products?.join(' | ')
        ]);
        const csvContent = [headers, ...rows].map(e => e.join(";")).join("\n");
        const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Parcours_${docName.replace(/\s+/g, '_')}.csv`;
        link.click();
    };


    const handleExportFrequentVisits = () => {
        if (!frequentVisitsAlerts || frequentVisitsAlerts.length === 0) {
            message.warning("Aucune visite rapprochée à exporter.");
            return;
        }

        const headers = [
            "Médecin",
            "Spécialité",
            "Région",
            "Partenaire",
            "Nombre Visites",
            "Écart Total",
            "Type Alerte",
            "Délégués Impliqués",
            "Visite N°",
            "Date et Heure Visite",
            "Délégué Visiteur",
            "Cycle Actif",
            "Semaine Cycle",
            "Période Semaine",
            "Statut Planning"
        ];

        const rows = [];

        frequentVisitsAlerts.forEach(item => {
            const alertType = item.isSameDelegate 
                ? `Même délégué (${item.delegatesNamesList?.[0] || item.delegate1Name})`
                : `Délégués différents (${item.uniqueDelegatesCount || 2})`;

            const delegatesList = item.delegatesNamesList ? item.delegatesNamesList.join(' & ') : `${item.delegate1Name} & ${item.delegate2Name}`;

            (item.visits || []).forEach(v => {
                let planningStatus = "Hors cycle actif";
                let cycleName = "N/A";
                let weekStr = "N/A";
                let periodStr = "N/A";

                if (v.context) {
                    cycleName = v.context.cycleName || "N/A";
                    weekStr = `Semaine ${v.context.weekNumber}`;
                    periodStr = `${v.context.startShort} au ${v.context.endShort}`;
                    if (v.context.isPlannedThisWeek) {
                        planningStatus = "Prévu cette semaine";
                    } else if (v.context.plannedInOtherWeek) {
                        planningStatus = `Prévu en Semaine ${v.context.plannedInOtherWeek}`;
                    } else {
                        planningStatus = "Hors planning cycle";
                    }
                }

                rows.push([
                    `"${item.name || ''}"`,
                    `"${item.specialtyName || ''}"`,
                    `"${item.regionName || ''}"`,
                    item.isPartner ? "OUI" : "NON",
                    item.totalVisitsCount || 2,
                    `"${item.totalSpanDays || item.daysDiff}"`,
                    `"${alertType}"`,
                    `"${delegatesList}"`,
                    `Visite ${v.visitIndex || 1}`,
                    `"${v.dateFull || ''}"`,
                    `"${v.delegateName || ''}"`,
                    `"${cycleName}"`,
                    `"${weekStr}"`,
                    `"${periodStr}"`,
                    `"${planningStatus}"`
                ]);
            });
        });

        const csvContent = [headers.join(";"), ...rows.map(r => r.join(";"))].join("\n");
        const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `visites_rapprochees_${dayjs().format('YYYY-MM-DD')}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        message.success("Export des visites rapprochées téléchargé avec succès !");
    };

    // Filtrage dynamique des régions selon la recherche
    const filteredRegions = Object.keys(regionAnalysis).filter(name =>
        name.toLowerCase().includes(regionSearch.toLowerCase().trim())
    );

    const kpiCardStyle = { 
        textAlign: 'center', borderRadius: 12, height: '130px', 
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: 'none' 
    };

    // --- COLONNES DU TABLEAU DE MONITORING (J-1 & HISTORIQUE) ---
    const monitoringColumns = [
        {
            title: 'Délégué',
            dataIndex: 'delegateName',
            key: 'delegateName',
            width: 200,
            render: (name) => <Text strong><UserOutlined style={{ marginRight: 6 }} />{name}</Text>
        },
        {
            title: 'Rapports Déposés',
            dataIndex: 'reportsCount',
            key: 'reportsCount',
            align: 'center',
            width: 140,
            render: (count) => (
                <Tag color={count > 0 ? "blue" : "default"} style={{ fontSize: '13px', fontWeight: 'bold' }}>
                    {count} visite{count > 1 ? 's' : ''}
                </Tag>
            )
        },
        {
            title: 'Régions Visitées (Prévues)',
            dataIndex: 'inPlanRegions',
            key: 'inPlanRegions',
            render: (regions) => (
                regions && regions.length > 0 ? (
                    <Space wrap size={[4, 4]}>
                        {regions.map((r, i) => (
                            <Tag key={i} color="success" icon={<CheckCircleOutlined />}>{r}</Tag>
                        ))}
                    </Space>
                ) : <Text type="secondary">-</Text>
            )
        },
        {
            title: 'Régions HORS Planning Semaine',
            dataIndex: 'offPlanRegions',
            key: 'offPlanRegions',
            render: (offRegions, record) => (
                offRegions && offRegions.length > 0 ? (
                    <Space wrap size={[4, 4]}>
                        {offRegions.map((r, i) => {
                            const count = record.offPlanDetails?.[r]?.length || 0;
                            return (
                                <Tooltip key={i} title="Cliquez pour voir les médecins visités et leur vrai planning">
                                    <Tag 
                                        color="error" 
                                        icon={<WarningOutlined />} 
                                        style={{ fontWeight: 'bold', cursor: 'pointer', padding: '2px 8px' }}
                                        onClick={() => {
                                            setSelectedOffPlanInfo({
                                                region: r,
                                                delegateName: record.delegateName,
                                                currentCycleInfo: record.currentCycleInfo, 
                                                doctors: record.offPlanDetails?.[r] || []
                                            });
                                            setOffPlanModalVisible(true);
                                        }}
                                    >
                                        {r} ({count}) 🔍
                                    </Tag>
                                </Tooltip>
                            );
                        })}
                    </Space>
                ) : <Tag color="default" style={{ color: '#52c41a' }}>Aucune (Conforme)</Tag>
            )
        },
        {
            title: 'Contrôle Superviseur',
            dataIndex: 'supervisorVisitsCount',
            key: 'supervisorVisitsCount',
            align: 'center',
            width: 190,
            render: (count, record) => (
                count > 0 ? (
                    <Button 
                        size="small" 
                        style={{ 
                            backgroundColor: '#f9f0ff', 
                            borderColor: '#d3adf7', 
                            color: '#722ed1',
                            fontWeight: 'bold' 
                        }}
                        onClick={() => handleOpenSupervisorVisits(record.delegateName, record.supervisorVisits)}
                    >
                        👑 {count} contrôle{count > 1 ? 's' : ''} J-1
                    </Button>
                ) : (
                    <Text type="secondary" style={{ fontSize: '12px' }}>Aucun</Text>
                )
            )
        }
    ];

    const formattedDailyTrend = React.useMemo(() => {
        if (!dailyTrend || dailyTrend.length === 0) return [];

        const list = [];
        for (let i = 0; i < dailyTrend.length; i++) {
            const current = dailyTrend[i];
            list.push(current);

            const next = dailyTrend[i + 1];
            if (next) {
                const curDay = String(current.day || '').toLowerCase();
                const nextDay = String(next.day || '').toLowerCase();

                // Détecte Vendredi suivi de Lundi
                const isFri = curDay.includes('fri') || curDay.includes('ven');
                const isMon = nextDay.includes('mon') || nextDay.includes('lun');

                if (isFri && isMon) {
                    list.push({
                        day: 'Weekend',
                        fullDate: 'Weekend (Samedi & Dimanche)',
                        visites: 0,
                        regions: [],
                        delegates: []
                    });
                }
            }
        }

        return list.map(item => {
            const d = String(item.day || '').toLowerCase().trim();
            if (d === '' || d.includes('sam') || d.includes('dim') || d.includes('sat') || d.includes('sun')) {
                return { ...item, day: 'Weekend', fullDate: 'Weekend (Samedi & Dimanche)', visites: 0 };
            }
            return item;
        });
    }, [dailyTrend]);
    return (
        <div style={{ padding: '25px', background: '#f0f2f5', minHeight: '100vh' }}>
            
            <style>{`
                .recharts-tooltip-wrapper {
                    z-index: 9999 !important;
                    pointer-events: auto !important;
                }
                .search-region-input input {
                    height: 36px !important;
                    line-height: 36px !important;
                    padding-top: 0 !important;
                    padding-bottom: 0 !important;
                    vertical-align: middle !important;
                }
                .search-region-input .ant-input-group-addon button {
                    height: 36px !important;
                }
                .custom-tooltip-scroll::-webkit-scrollbar {
                    width: 5px;
                }
                .custom-tooltip-scroll::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 4px;
                }
                .custom-tooltip-scroll::-webkit-scrollbar-thumb {
                    background: #bfbfbf;
                    border-radius: 4px;
                }
                .custom-tooltip-scroll::-webkit-scrollbar-thumb:hover {
                    background: #888;
                }
            `}</style>

            {/* --- HEADER & FILTRES --- */}
            <div style={{ marginBottom: 25, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0 }}>Tableau de Bord Intelligence</Title>
                    <Text type="secondary">Analyse de la performance et couverture géographique</Text>
                </div>
                <Button type="primary" icon={<ReloadOutlined />} onClick={loadData} loading={loading}>Actualiser</Button>
            </div>

            {/* --- HEADER & FILTRES GLOBAUX --- */}
            <Card style={{ marginBottom: 25, borderRadius: 12, border: 'none' }}>
                <Row gutter={16} align="bottom">
                    {/* 1. Période */}
                    <Col xs={24} sm={12} md={6}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Période :</Text>
                        <RangePicker style={{ width: '100%' }} value={dateRange} onChange={setDateRange} format="DD/MM/YYYY" />
                    </Col>

                    {/* 2. NOUVEAU : Filtre Gamme / Catégorie */}
                    <Col xs={24} sm={12} md={5}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Gamme :</Text>
                        <Select 
                            style={{ width: '100%' }} 
                            value={selectedCategory} 
                            onChange={(val) => {
                                setSelectedCategory(val);
                                setSelectedDelegate(null); // Réinitialise le délégué choisi
                            }}
                        >
                            <Option value="all">Toutes les gammes</Option>
                            <Option value="AKTIV"><Tag color="blue" style={{ margin: 0, fontWeight: 'bold' }}>AKTIV</Tag></Option>
                            <Option value="SANOSAN"><Tag color="green" style={{ margin: 0, fontWeight: 'bold' }}>SANOSAN</Tag></Option>
                            <Option value="MATERNITE"><Tag color="purple" style={{ margin: 0, fontWeight: 'bold' }}>MATERNITE</Tag></Option>
                        </Select>
                    </Col>

                    {/* 3. Délégué (filtré automatiquement selon la gamme sélectionnée) */}
                    <Col xs={24} sm={12} md={6}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Délégué :</Text>
                        <Select 
                            showSearch
                            optionFilterProp="label"
                            style={{ width: '100%' }} 
                            placeholder="Tous les délégués"
                            value={selectedDelegate}
                            onChange={setSelectedDelegate}
                            allowClear
                        >
                            {delegates
                                .filter(d => !d.excludeFromStats) // <--- NE GARDER QUE CEUX QUI SONT INCLUS
                                .filter(d => selectedCategory === 'all' || d.category === selectedCategory)
                                .map(d => (
                                    <Option key={d.id} value={d.id} label={`${d.firstname} ${d.lastname}`}>
                                        {d.firstname} {d.lastname} {d.category ? `(${d.category})` : ''}
                                    </Option>
                                ))
                            }
                        </Select>
                    </Col>

                    {/* 4. Boutons */}
                    <Col xs={24} sm={12} md={24} lg={7} className="filter-actions-col">
                        <Space wrap>
                            <Button type="primary" icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
                                Actualiser
                            </Button>
                            <Button icon={<DownloadOutlined />}>Rapport Complet</Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Spin spinning={loading} size="large">
                {/* --- KPI ROW --- */}
               <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
                    <Col xs={12} sm={12} md={8} lg={5} xl={5}>
                        <Card style={kpiCardStyle} hoverable>
                            <Statistic 
                                title="VISITES TOTALES / CIBLE" 
                                value={`${kpis?.visitsYesterday ?? 0} / ${kpis?.totalPlanned ?? 0}`} 
                                prefix={<TeamOutlined style={{ color: '#1890ff' }} />} 
                            />
                        </Card>
                    </Col>
                   <Col xs={12} sm={12} md={8} lg={5} xl={5}>
                        <Card style={kpiCardStyle} hoverable>
                            <Statistic title="TAUX RÉALISATION" value={kpis?.realizationRate || 0} suffix="%" valueStyle={{ color: '#52c41a' }} />
                        </Card>
                    </Col>
                    <Col xs={12} sm={12} md={8} lg={4} xl={4}>
                        <Card style={kpiCardStyle} hoverable>
                            <Statistic title="INDEX RETOUR" value={kpis?.returnIndex || 0} prefix={<SyncOutlined spin={loading} style={{ color: '#faad14' }} />} />
                        </Card>
                    </Col>
                   <Col xs={12} sm={12} md={8} lg={5} xl={5}>
                        <Card style={kpiCardStyle} hoverable>
                            <Statistic title="VISITES RATÉES" value={kpis?.missedVisitsWeek || 0} valueStyle={{ color: '#f5222d' }} />
                        </Card>
                    </Col>
                      <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                        <Card style={kpiCardStyle} hoverable>
                            <Statistic title="NOUVELLES CRÉATIONS" value={kpis?.newCreations || 0} prefix={<UserAddOutlined style={{ color: '#722ed1' }} />} />
                        </Card>
                    </Col>
                </Row>


                <Row gutter={24} style={{ marginBottom: 25 }}>
                    <Col span={24}>
                        <Collapse
                            defaultActiveKey={['1']}
                            style={{ 
                                borderRadius: 12, 
                                border: 'none', 
                                backgroundColor: '#ffffff',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)' 
                            }}
                            items={[
                                {
                                    key: '1',
                                    label: (
                                        <Space>
                                            <MedicineBoxOutlined style={{ color: '#f5222d', fontSize: '18px' }} />
                                            <span style={{ fontWeight: 'bold', fontSize: '15px' }}>
                                                Contrôle Semaine Précédente ({prevWeekData?.periodLabel}) : Visites Manquées & Visites Hors Planning
                                            </span>
                                        </Space>
                                    ),
                                    extra: (
                                        <Space>
                                            {/* NOUVEAU BOUTON : Ouvre la modale d'historique */}
                                            <Button 
                                                type="primary" 
                                                ghost 
                                                size="small" 
                                                icon={<HistoryOutlined />} 
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Évite que l'accordéon se referme au clic
                                                    setPrevWeekHistoryModalVisible(true);
                                                    fetchHistoryWeekCompliance(historyWeekRange);
                                                }}
                                            >
                                                Historique des semaines
                                            </Button>

                                            {/* BOUTON EXISTANT : Actualise la semaine courante */}
                                            <Button 
                                                size="small" 
                                                icon={<ReloadOutlined />} 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    fetchPrevWeekCompliance();
                                                }}
                                            >
                                                Actualiser
                                            </Button>
                                        </Space>
                                    ),
                                    children: (
                                        <Table 
                                            dataSource={prevWeekData?.delegates || []}
                                            columns={prevWeekColumns}
                                            rowKey="delegateId"
                                            loading={loadingPrevWeek}
                                            pagination={false}
                                            size="middle"
                                            scroll={{ x: 900, y: 350 }} 
                                            locale={{ emptyText: "Aucune donnée disponible pour la semaine passée." }}
                                        />
                                    )
                                }
                            ]}
                        />
                    </Col>
                </Row>

                {/* --- NOUVEAU BLOC : ACTIVITÉ J-1 & RÉGIONS HORS PLANNING --- */}
                <Row gutter={24} style={{ marginBottom: 25 }}>
                    <Col span={24}>
                        <Collapse
                            defaultActiveKey={['1']} // Ouvert par défaut (retirez ['1'] si vous le voulez fermé par défaut)
                            style={{ 
                                borderRadius: 12, 
                                border: 'none', 
                                backgroundColor: '#ffffff',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)' 
                            }}
                            items={[
                                {
                                    key: '1',
                                    label: (
                                        <Space>
                                            <CompassOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                                            <span style={{ fontWeight: 'bold', fontSize: '15px' }}>
                                                {dailyMonitoring?.isFridayShift 
                                                    ? `Activité du dernier jour ouvré (Vendredi : ${dailyMonitoring?.displayDate}) & Respect du Planning`
                                                    : `Activité de la veille (J-1 : ${dailyMonitoring?.displayDate || dayjs().subtract(1, 'day').format('DD/MM/YYYY')}) & Respect du Planning`
                                                }
                                            </span>
                                        </Space>
                                    ),
                                    extra: (
                                        <Button 
                                            type="primary" 
                                            ghost 
                                            size="small"
                                            icon={<HistoryOutlined />} 
                                            onClick={(e) => {
                                                e.stopPropagation(); // Évite de fermer/ouvrir l'accordéon en cliquant sur le bouton
                                                setHistoryModalVisible(true);
                                                fetchHistoryMonitoring(historyDate);
                                            }}
                                        >
                                            Historique des jours précédents
                                        </Button>
                                    ),
                                    children: (
                                        <Table 
                                            dataSource={dailyMonitoring?.delegates || []}
                                            columns={monitoringColumns}
                                            rowKey="delegateId"
                                            loading={loadingDaily}
                                            pagination={false}
                                            size="middle"
                                            scroll={{ x: 850, y: 350 }}
                                            locale={{ emptyText: "Aucune donnée enregistrée hier." }}
                                        />
                                    )
                                }
                            ]}
                        />
                    </Col>
                </Row>

                {/* --- ANALYSE RÉGIONALE --- */}
                <Row gutter={24} style={{ marginBottom: 25 }}>
                    <Col span={24}>
                        <Card 
                            title={<Space><GlobalOutlined style={{ color: '#1890ff' }} /> <span>Analyse Profonde : Spécialités visitées par Région</span></Space>}
                            style={{ borderRadius: 12, border: 'none' }}
                        >
                            <Row gutter={40}>
                                <Col xs={24} lg={8} style={{ borderRight: '1px solid #f0f0f0' }}>
                                    <div style={{ marginBottom: 10 }}>
                                        <Text strong type="secondary">Villes visitées sur la période choisie :</Text>
                                    </div>
                                    
                                    <div style={{ marginBottom: 15 }}>
                                        <Input.Search
                                            className="search-region-input"
                                            placeholder="Filtrer une région / ville..."
                                            allowClear
                                            value={regionSearch}
                                            onChange={(e) => setRegionSearch(e.target.value)}
                                            style={{ width: '100%' }}
                                        />
                                    </div>

                                    <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                        <List
                                            size="small"
                                            dataSource={filteredRegions}
                                            locale={{ emptyText: regionSearch ? "Aucune région ne correspond" : "Aucune donnée géographique" }}
                                            renderItem={name => (
                                                <List.Item 
                                                    style={{ 
                                                        cursor: 'pointer', 
                                                        border: 'none',
                                                        borderRadius: 8,
                                                        marginBottom: 5,
                                                        background: selectedRegion === name ? '#e6f7ff' : 'transparent',
                                                        padding: '10px 15px'
                                                    }}
                                                    onClick={() => setSelectedRegion(name)}
                                                >
                                                    <Flex justify="space-between" align="center" style={{ width: '100%' }}>
                                                        <Space>
                                                            <EnvironmentOutlined style={{ color: selectedRegion === name ? '#1890ff' : '#d9d9d9' }} />
                                                            <Text strong={selectedRegion === name}>{name}</Text>
                                                        </Space>
                                                        <Badge 
                                                            count={regionAnalysis[name]?.reduce((acc, curr) => acc + curr.count, 0) || 0} 
                                                            style={{ backgroundColor: selectedRegion === name ? '#1890ff' : '#bfbfbf' }} 
                                                        />
                                                    </Flex>
                                                </List.Item>
                                            )}
                                        />
                                    </div>
                                </Col>

                                <Col xs={24} lg={16}>
                                    {selectedRegion && regionAnalysis[selectedRegion] ? (
                                        <div style={{ textAlign: 'center' }}>
                                            <Title level={4} style={{ marginBottom: 20 }}>
                                                Répartition des spécialités : <span style={{ color: '#1890ff' }}>{selectedRegion.toUpperCase()}</span>
                                            </Title>
                                            <ResponsiveContainer width="100%" height={380}>
                                                <PieChart>
                                                    <Pie
                                                        data={regionAnalysis[selectedRegion]}
                                                        cx="50%" cy="50%"
                                                        innerRadius={80}
                                                        outerRadius={130}
                                                        paddingAngle={5}
                                                        dataKey="count"
                                                        nameKey="specialty"
                                                        label={({ specialty, percent }) => `${specialty} ${(percent * 100).toFixed(0)}%`}
                                                    >
                                                        {regionAnalysis[selectedRegion].map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <ChartTooltip />
                                                    <Legend verticalAlign="bottom" align="center" />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    ) : (
                                        <div style={{ padding: '100px 0' }}>
                                            <Empty description="Sélectionnez une région à gauche pour voir l'analyse détaillée" />
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>

                {/* --- RANGKING & TREND --- */}
                <Row gutter={[16, 16]} style={{ marginBottom: 25 }}>
                    <Col xs={24} xl={15}  style={{ position: 'relative', zIndex: 10 }}>
                        <Card title="Comparatif Réalisé vs Objectif" style={{ borderRadius: 12, border: 'none', position: 'relative',  zIndex: 10 }}>
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart data={rankings}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis 
                                        dataKey="name" 
                                        interval={0} 
                                        angle={-45} 
                                        textAnchor="end" 
                                        height={90} 
                                        tick={{ fontSize: 9 }} 
                                    />
                                    <YAxis />
                                    <ChartTooltip 
                                        content={<CustomTooltip />}  
                                        wrapperStyle={{ pointerEvents: 'auto' }} 
                                        allowEscapeViewBox={{ x: true, y: true }}  
                                        offset={-5}                               
                                    />
                                    <Legend verticalAlign="top" align="right" />
                                    <Bar name="Cible (Planning)" dataKey="planned" fill="#7f8183" radius={[4, 4, 0, 0]} />
                                    <Bar name="Fait (Visites)" dataKey="realized" fill="#1890ff" radius={[4, 4, 0, 0]} style={{ cursor: 'pointer' }} onClick={(entry) => setSelectedBarDelegate(entry)} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Card>
                    </Col>
                    <Col  xs={24} xl={9}>
                        <Card title="Classement des Scores (40/30/30)" style={{ borderRadius: 10 }}>
                            <Table 
                                dataSource={rankings} 
                                pagination={false} 
                                size="small" 
                                rowKey="delegateId" 
                                scroll={{ y: 350 }}
                                columns={[
                                { 
                                    title: <Text strong style={{ color: '#000' }}>Délégué</Text>, 
                                    dataIndex: 'name', 
                                    key: 'name', 
                                    fixed: 'left',
                                    width: 150,
                                    render: (t, r, i) => (
                                        <span style={{ fontSize: '11px', fontWeight: 'bold' }}>
                                            {i + 1}. {t} {i === 0 && '🏆'}
                                        </span>
                                    ) 
                                },
                                { 
                                    title: (
                                        <Tooltip 
                                            placement="top"
                                            title={
                                                <div>
                                                    <b>VOLUME (Score / 40)</b><br/>
                                                    <i>Mesure la quantité de travail réalisé.</i><br/>
                                                    <span style={{ color: '#faad14' }}>Équation : (Visites / (Jours ouvrés × Obj/Jour)) × 40</span>
                                                </div>
                                            }
                                        >
                                            <Space style={{ cursor: 'help' }}>
                                                <span style={{ color: '#000', fontWeight: 'bold' }}>Vol.</span>
                                                <InfoCircleOutlined style={{ fontSize: '10px', color: '#1890ff' }} />
                                            </Space>
                                        </Tooltip>
                                    ), 
                                    dataIndex: 'volPoints', 
                                    align: 'center', 
                                    width: 70,
                                    render: (v) => <Text strong style={{ color: '#1890ff' }}>{v}</Text> 
                                },
                                { 
                                    title: (
                                        <Tooltip 
                                            placement="top"
                                            title={
                                                <div>
                                                    <b>RIGUEUR (Score / 30)</b><br/>
                                                    <i>Mesure le respect du planning établi.</i><br/>
                                                    <span style={{ color: '#faad14' }}>Équation : (% de médecins prévus visités) × 0.3</span>
                                                </div>
                                            }
                                        >
                                            <Space style={{ cursor: 'help' }}>
                                                <span style={{ color: '#000', fontWeight: 'bold' }}>Taux</span>
                                                <InfoCircleOutlined style={{ fontSize: '10px', color: '#52c41a' }} />
                                            </Space>
                                        </Tooltip>
                                    ), 
                                    dataIndex: 'tauxPoints', 
                                    align: 'center', 
                                    width: 75,
                                    render: (t) => <Text strong style={{ color: '#52c41a' }}>{t}</Text> 
                                },
                                { 
                                    title: (
                                        <Tooltip 
                                            placement="top"
                                            title={
                                                <div>
                                                    <b>IMPACT (Score / 30)</b><br/>
                                                    <i>Mesure la répétition et le suivi.</i><br/>
                                                    <span style={{ color: '#faad14' }}>Équation : (Total Visites / Total Assignés) × 30</span>
                                                </div>
                                            }
                                        >
                                            <Space style={{ cursor: 'help' }}>
                                                <span style={{ color: '#000', fontWeight: 'bold' }}>Idx</span>
                                                <InfoCircleOutlined style={{ fontSize: '10px', color: '#faad14' }} />
                                            </Space>
                                        </Tooltip>
                                    ), 
                                    dataIndex: 'idxPoints', 
                                    align: 'center', 
                                    width: 65,
                                    render: (i) => <Text strong style={{ color: '#faad14' }}>{i}</Text> 
                                },
                                { 
                                    title: <Text strong style={{ color: '#000' }}>Total</Text>, 
                                    dataIndex: 'totalScore', 
                                    align: 'right', 
                                    width: 90,
                                    render: (s) => (
                                        <Tag color="blue" style={{ fontWeight: 'bold', borderRadius: '4px', margin: 0, width: '100%', textAlign: 'center' }}>
                                            {s} / 100
                                        </Tag>
                                    ) 
                                }
                            ]}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* --- GRAPHIQUE ÉVOLUTION QUOTIDIENNE --- */}
                <Row gutter={24} style={{ marginBottom: 25 }}>
                    <Col span={24}>
                        <Card title="Activité journalière de l'équipe (5 derniers jours)" style={{ borderRadius: 10 }}>
                            <div style={{ width: '100%', height: 300 }}>
                                <ResponsiveContainer>
                                    <BarChart data={formattedDailyTrend}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="day" interval={0} />
                                    <YAxis />
                                    <ChartTooltip content={<CustomTooltip />} cursor={{ fill: '#f8f9fa' }} />

                                    {/* LIGNE FINE DE SÉPARATION DU WEEKEND ENTRE VENDREDI ET LUNDI */}
                                    <ReferenceLine 
                                        x="04/09 (Fri)" 
                                        stroke="#fa8c16" 
                                        strokeDasharray="4 4" 
                                        strokeWidth={2}
                                        label={{ 
                                            value: ' Weekend ', 
                                            position: 'top', 
                                            fill: '#fa8c16', 
                                            fontSize: 12, 
                                            fontWeight: 'bold',
                                            offset: 10
                                        }} 
                                    />

                                    <Bar name="Visites totales" dataKey="visites" fill="#52c41a" radius={[4, 4, 0, 0]} barSize={45} />
                                </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* --- BLOC DES 3 ALERTES CÔTE À CÔTE (33% CHACUNE) --- */}
                <Row gutter={16} style={{ marginBottom: 25 }}>
    
                    {/* 1. Alerte Médecins Partenaires (33%) */}
                    <Col xs={24} lg={8}>
                        <Card 
                            title={
                                <Space>
                                    <AlertOutlined style={{ color: '#f5222d' }} />
                                    <span style={{ color: '#f5222d', fontWeight: 'bold', fontSize: '13px' }}>
                                        Partenaires non visités (30j+)
                                    </span>
                                    <Badge count={alerts.length} style={{ backgroundColor: '#f5222d' }} />
                                </Space>
                            }
                            style={{ borderRadius: 12, border: '1px solid #ffa39e', background: '#fff2f0', height: '100%' }}
                        >
                            {alerts.length > 0 ? (
                                <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                                    <List
                                        size="small"
                                        dataSource={alerts}
                                        renderItem={item => (
                                            <List.Item
                                                actions={[
                                                    <Button 
                                                        size="small" 
                                                        type="primary" 
                                                        ghost
                                                        icon={<AuditOutlined />}
                                                        onClick={() => handleOpenDoctorHistory(item.id)}
                                                    >
                                                        Parcours
                                                    </Button>
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    avatar={
                                                        <Avatar 
                                                            icon={<UserOutlined />} 
                                                            style={{ backgroundColor: '#ffccc7', color: '#f5222d', cursor: 'pointer' }} 
                                                            onClick={() => handleOpenDoctorHistory(item.id)}
                                                        />
                                                    }
                                                    title={
                                                        <Space wrap align="center">
                                                            <Text strong style={{ cursor: 'pointer', color: '#1890ff', fontSize: '13px' }} onClick={() => handleOpenDoctorHistory(item.id)}>
                                                                {item.name}
                                                            </Text>
                                                            {item.delegates?.map((delName, idx) => (
                                                                <Tag key={idx} color="blue" style={{ fontSize: '10px' }}>{delName}</Tag>
                                                            ))}
                                                        </Space>
                                                    }
                                                    description={
                                                        <Space>
                                                            <Tag color="error" style={{ fontSize: '10px' }}>{item.lastVisit === 'Aucune visite' ? 'Jamais visité' : item.lastVisit}</Tag>
                                                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                                                {item.daysInactive >= 999 ? '(Aucune couverture)' : `(${item.daysInactive} j)`}
                                                            </Text>
                                                        </Space>
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </div>
                            ) : (
                                <Empty description="Tous vos partenaires sont à jour !" />
                            )}
                        </Card>
                    </Col>

                    {/* 2. Alerte Régions Délaissées en 2026 */}
                    <Col xs={24} lg={8}>
                        <Card 
                            title={
                                <Space>
                                    <EnvironmentOutlined style={{ color: '#fa8c16' }} />
                                    <span style={{ color: '#d46b08', fontWeight: 'bold', fontSize: '13px' }}>
                                        Régions non couvertes en 2026 (30j+)
                                    </span>
                                    <Badge count={regionAlerts.length} style={{ backgroundColor: '#fa8c16' }} />
                                </Space>
                            }
                            style={{ borderRadius: 12, border: '1px solid #ffd591', background: '#fffbe6', height: '100%' }}
                        >
                            {regionAlerts.length > 0 ? (
                                <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                                    <List
                                        size="small"
                                        dataSource={regionAlerts}
                                        renderItem={item => (
                                            <List.Item>
                                                <List.Item.Meta
                                                    avatar={
                                                        <Avatar 
                                                            icon={<EnvironmentOutlined />} 
                                                            style={{ backgroundColor: '#ffe7ba', color: '#fa8c16' }} 
                                                        />
                                                    }
                                                    title={<Text strong style={{ fontSize: '13px' }}>{item.name}</Text>}
                                                    description={
                                                        <Space align="center">
                                                            <Tag color="warning" style={{ fontSize: '10px' }}>
                                                                {item.lastVisit}
                                                            </Tag>
                                                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                                                {item.daysInactive >= 999 ? '(Aucun passage en 2026)' : `(${item.daysInactive} j sans passage)`}
                                                            </Text>
                                                        </Space>
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </div>
                            ) : (
                                <Empty description="Toutes les régions sont couvertes en 2026 !" />
                            )}
                        </Card>
                    </Col>

                    {/* 3. NOUVELLE CARTE : Visites Rapprochées / Doublons < 5j (33%) */}
                    <Col xs={24} lg={8}>
                        <Card 
                            title={
                                <Space>
                                    <ClockCircleOutlined style={{ color: '#c41d7f' }} />
                                    <span style={{ color: '#c41d7f', fontWeight: 'bold', fontSize: '13px' }}>
                                        Visites Rapprochées (&lt; 5j)
                                    </span>
                                    <Badge count={frequentVisitsAlerts.length} style={{ backgroundColor: '#eb2f96' }} />
                                </Space>
                            }

                            extra={
                                <Button 
                                    size="small" 
                                    icon={<DownloadOutlined />} 
                                    onClick={handleExportFrequentVisits}
                                    disabled={!frequentVisitsAlerts || frequentVisitsAlerts.length === 0}
                                    style={{ borderColor: '#ffadd2', color: '#c41d7f', fontSize: '11px', padding: '0 8px' }}
                                >
                                    Exporter
                                </Button>
                            }
                            style={{ borderRadius: 12, border: '1px solid #ffadd2', background: '#fff0f6', height: '100%' }}
                        >
                            {frequentVisitsAlerts.length > 0 ? (
                                <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                                    <List
                                        size="small"
                                        dataSource={frequentVisitsAlerts}
                                        renderItem={item => (
                                            <List.Item
                                                style={{ 
                                                    padding: '10px 0', 
                                                    alignItems: 'flex-start' // Aligne le bouton proprement en haut
                                                }}
                                                actions={[
                                                    <Button 
                                                        size="small" 
                                                        type="primary" 
                                                        ghost
                                                        icon={<AuditOutlined />}
                                                        onClick={() => handleOpenDoctorHistory(item.personId)}
                                                        style={{ padding: '0 8px' }}
                                                    >
                                                        Parcours
                                                    </Button>
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    // FIX ANTI-CHEVAUCHEMENT : Empêche le texte de déborder sur le bouton
                                                    style={{ maxWidth: 'calc(100% - 90px)', margin: 0 }}
                                                    avatar={
                                                        <Avatar 
                                                            icon={<UserOutlined />} 
                                                            style={{ backgroundColor: '#ffd6e7', color: '#c41d7f', cursor: 'pointer' }} 
                                                            onClick={() => handleOpenDoctorHistory(item.personId)}
                                                        />
                                                    }
                                                    title={
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                            <Text 
                                                                strong 
                                                                style={{ cursor: 'pointer', color: '#1890ff', fontSize: '13px' }} 
                                                                onClick={() => handleOpenDoctorHistory(item.personId)}
                                                            >
                                                                {item.name}
                                                            </Text>
                                                            {item.isPartner && <StarFilled style={{ color: '#fadb14', fontSize: '12px' }} />}
                                                        </div>
                                                    }
                                                    description={
                                                        <div style={{ fontSize: '11px', marginTop: 4 }}>
                                                            {/* 1. RÉSUMÉ DU GROUPE : NOMBRE DE VISITES ET ÉCART TOTAL */}
                                                            <div style={{ marginBottom: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                                                <Tag color="error" style={{ fontSize: '10px', fontWeight: 'bold', margin: 0 }}>
                                                                    ⚠️ {item.totalVisitsCount} visites en {item.totalSpanDays}
                                                                </Tag>

                                                                {item.isSameDelegate ? (
                                                                    <Tag color="magenta" style={{ fontSize: '10px', fontWeight: 'bold', margin: 0 }}>
                                                                        Même délégué : {item.delegatesNamesList[0]}
                                                                    </Tag>
                                                                ) : (
                                                                    <Tag color="purple" style={{ fontSize: '10px', fontWeight: 'bold', margin: 0 }}>
                                                                        👥 {item.uniqueDelegatesCount} délégués différents
                                                                    </Tag>
                                                                )}
                                                            </div>

                                                            {/* 2. LISTE CHRONOLOGIQUE DE TOUTES LES VISITES DU MÉDECIN (2, 3 ou plus) */}
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                {item.visits?.map((v, vIdx) => (
                                                                    <div 
                                                                        key={vIdx} 
                                                                        style={{ 
                                                                            background: '#ffffff', 
                                                                            padding: '4px 8px', 
                                                                            borderRadius: 4, 
                                                                            border: vIdx % 2 === 0 ? '1px solid #ffd6e7' : '1px solid #efdbff' 
                                                                        }}
                                                                    >
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                            <Text strong style={{ color: vIdx % 2 === 0 ? '#c41d7f' : '#531dab', fontSize: '10px' }}>
                                                                                Visite {v.visitIndex} ({v.dateFull}) - {v.delegateName} :
                                                                            </Text>
                                                                        </div>

                                                                        {v.context ? (
                                                                            <div style={{ marginTop: 2, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                                                                                <Tag color="geekblue" style={{ fontSize: '9px', margin: 0 }}>
                                                                                    {v.context.cycleName} - S{v.context.weekNumber} ({v.context.startShort} au {v.context.endShort})
                                                                                </Tag>
                                                                                {v.context.isPlannedThisWeek ? (
                                                                                    <Tag color="green" style={{ fontSize: '9px', margin: 0 }}>✅ Prévu cette semaine</Tag>
                                                                                ) : v.context.plannedInOtherWeek ? (
                                                                                    <Tag color="orange" style={{ fontSize: '9px', margin: 0 }}>⚠️ Prévu en S{v.context.plannedInOtherWeek}</Tag>
                                                                                ) : (
                                                                                    <Tag color="default" style={{ fontSize: '9px', margin: 0 }}>❌ Hors planning cycle</Tag>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <Text type="secondary" style={{ fontSize: '10px' }}>Hors cycle actif à cette date</Text>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </div>
                            ) : (
                                <Empty description="Aucune visite en doublon (< 5 jours) détectée !" />
                            )}
                        </Card>
                    </Col>

                </Row>
            </Spin>

            {/* --- MODALE HISTORIQUE DES JOURS PRÉCÉDENTS --- */}
            <Modal
                title={
                    <Space>
                        <HistoryOutlined style={{ color: '#1890ff' }} />
                        <span>Historique & Audit d'Activité Journalière</span>
                    </Space>
                }
                open={historyModalVisible}
                onCancel={() => setHistoryModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setHistoryModalVisible(false)}>
                        Fermer
                    </Button>
                ]}
                width={1000}
            >
                <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 15 }}>
                    <Text strong>Choisir une date à auditer :</Text>
                    <DatePicker 
                        value={historyDate} 
                        onChange={(d) => {
                            setHistoryDate(d);
                            fetchHistoryMonitoring(d);
                        }} 
                        format="DD/MM/YYYY"
                        allowClear={false}
                    />
                    <Button type="primary" icon={<ReloadOutlined />} onClick={() => fetchHistoryMonitoring(historyDate)}>
                        Actualiser
                    </Button>
                </div>

                <Table 
                    dataSource={historyData?.delegates || []}
                    columns={monitoringColumns}
                    rowKey="delegateId"
                    loading={loadingHistory}
                    pagination={false}
                    size="small"
                    scroll={{ y: 400 }}
                />
            </Modal>

            {/* --- MODALE PARCOURS DE VISITE DU MÉDECIN --- */}
            <Modal 
                title={ 
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '95%', alignItems: 'center' }}> 
                        <span><AuditOutlined /> Parcours de visite du Médecin</span> 
                        <Button 
                            icon={<DownloadOutlined />} 
                            size="small" 
                            type="primary" 
                            onClick={handleExportDoctorHistory} 
                            disabled={doctorDetailData.length === 0}
                        >
                            Exporter Historique
                        </Button> 
                    </div> 
                } 
                open={doctorModalVisible} 
                onCancel={() => setDoctorModalVisible(false)} 
                footer={null} 
                width={1100}
                zIndex={2000}
            >
                <Spin spinning={doctorLoading}>
                    {doctorDetailData.length > 0 && (
                        (() => {
                            const firstRecord = doctorDetailData[0];
                            const isPartner = firstRecord.isPartner === true || firstRecord.isPartner === 1 || firstRecord.isPartner === 'true';

                            return (
                                <div style={{ 
                                    marginBottom: 20, 
                                    padding: '20px', 
                                    borderRadius: '12px', 
                                    backgroundColor: isPartner ? '#fffbe6' : '#f0f5ff', 
                                    border: isPartner ? '2px solid #fadb14' : '1px solid #adc6ff',
                                    boxShadow: isPartner ? '0 4px 12px rgba(250, 219, 20, 0.15)' : 'none'
                                }}>
                                    <Row gutter={[16, 16]} align="middle">
                                        <Col span={16}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                                                <UserOutlined style={{ fontSize: '28px', marginTop: '5px', color: isPartner ? '#856404' : '#1890ff' }} />
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                        <Title level={3} style={{ margin: 0, color: isPartner ? '#856404' : '#002766' }}>
                                                            {firstRecord.doctorName}
                                                        </Title>
                                                        
                                                        {isPartner && (
                                                            <Tag color="gold" style={{ 
                                                                fontSize: '13px', 
                                                                fontWeight: '900', 
                                                                border: '1px solid #d4b106', 
                                                                borderRadius: '4px',
                                                                padding: '2px 10px',
                                                                textTransform: 'uppercase'
                                                            }}>
                                                                <StarFilled style={{ marginRight: '6px' }} /> MÉDECIN PARTENAIRE
                                                            </Tag>
                                                        )}
                                                    </div>

                                                    <Space wrap size={8} style={{ marginTop: '10px' }}>
                                                        <Tag color="blue" style={{ fontWeight: 'bold' }}>{firstRecord.specialtyName}</Tag>
                                                        <Text type="secondary"><EnvironmentOutlined /> {firstRecord.regionName || "N/A"}</Text>
                                                    </Space>
                                                </div>
                                            </div>
                                        </Col>

                                        <Col span={8} style={{ 
                                            textAlign: 'right', 
                                            borderLeft: `2px solid ${isPartner ? '#fadb14' : '#adc6ff'}`, 
                                            paddingLeft: '20px' 
                                        }}>
                                            <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '8px' }}>
                                                Délégués assignés :
                                            </Text>
                                            <Space wrap size={[4, 8]}>
                                                {firstRecord.assignedDelegates?.map((name, idx) => (
                                                    <Tag key={idx} color="cyan" icon={<RocketOutlined />} style={{ borderRadius: '10px' }}>
                                                        {name.toUpperCase()}
                                                    </Tag>
                                                ))}
                                            </Space>
                                        </Col>
                                    </Row>

                                    {isPartner && (
                                        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px dashed #ffe58f' }}>
                                            <Text strong style={{ color: '#d4b106', display: 'block', marginBottom: '8px', fontSize: '13px' }}>
                                                <StarFilled /> Historique Partenaire :
                                            </Text>
                                            {firstRecord.partnerNotes ? (
                                                <List
                                                    size="small"
                                                    bordered
                                                    style={{ background: '#fffbe6', borderRadius: '6px', border: '1px solid #ffe58f' }}
                                                    dataSource={firstRecord.partnerNotes.split('\n').filter(Boolean)}
                                                    renderItem={(item) => (
                                                        <List.Item style={{ fontSize: '13px', color: '#856404', padding: '8px 14px' }}>
                                                            {item}
                                                        </List.Item>
                                                    )}
                                                />
                                            ) : (
                                                <Text type="secondary" italic style={{ fontSize: '12px', color: '#ad8b00' }}>
                                                    Aucune note partenaire enregistrée pour ce médecin.
                                                </Text>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })()
                    )}

                    <List 
                        dataSource={doctorDetailData} 
                        renderItem={item => (
                            <List.Item style={{ padding: '12px 15px', borderBottom: '1px solid #f0f0f0' }}>
                                <div style={{ display: 'flex', width: '100%', alignItems: 'start' }}>
                                    <div style={{ width: '20%' }}>
                                        <Text strong>{dayjs(item.reportDate).format('DD/MM/YYYY')}</Text>
                                        <br/>
                                        <Text type="secondary" style={{ fontSize: '11px' }}>{dayjs(item.reportDate).format('HH:mm')}</Text>
                                    </div>
                                    <div style={{ width: '25%' }}>
                                        <Tag icon={<UserOutlined />} color="default">{item.delegateName}</Tag>
                                    </div>
                                    <div style={{ width: '30%' }}> 
                                        {item.samples?.map((s, i) => (
                                            <Tag key={i} color={s.isSpecific ? "error" : "processing"} style={{ marginBottom: '4px' }}>
                                                {s.name} x{s.quantity}
                                            </Tag>
                                        ))}
                                    </div>
                                    <div style={{ width: '25%' }}> 
                                        {item.products?.map((p, i) => (
                                            <Tag key={i} color="orange" style={{ marginBottom: '4px' }}>{p}</Tag>
                                        ))}
                                    </div>
                                </div>
                            </List.Item>
                        )} 
                    />
                    {doctorDetailData.length === 0 && !doctorLoading && <Empty description="Aucune visite trouvée" />}
                </Spin>
            </Modal>

            {/* --- MODALE LISTE DES RÉGIONS DU DÉLÉGUÉ CLIQUÉ --- */}
            <Modal
                title={
                    <Space>
                        <EnvironmentOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                        <span>Régions visitées par : <b style={{ color: '#1890ff' }}>{selectedBarDelegate?.name}</b></span>
                        <Badge count={selectedBarDelegate?.regions?.length || 0} style={{ backgroundColor: '#52c41a' }} />
                    </Space>
                }
                open={!!selectedBarDelegate}
                onCancel={() => setSelectedBarDelegate(null)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setSelectedBarDelegate(null)}>
                        Fermer
                    </Button>
                ]}
                width={650}
            >
                <div style={{ padding: '10px 0' }}>
                    <p style={{ color: '#8c8c8c', marginBottom: 15 }}>
                        Total de <b>{selectedBarDelegate?.realized} visites</b> réalisées sur <b>{selectedBarDelegate?.regions?.length} régions</b>.
                    </p>

                    {selectedBarDelegate?.regions && selectedBarDelegate.regions.length > 0 ? (
                        <div style={{ maxHeight: '350px', overflowY: 'auto', paddingRight: '10px' }}>
                            <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(2, 1fr)', 
                                gap: '10px' 
                            }}>
                                {selectedBarDelegate.regions.map((regName, idx) => (
                                    <div 
                                        key={idx}
                                        style={{ 
                                            padding: '10px 14px',
                                            background: '#f5f7fa',
                                            border: '1px solid #e4e7ed',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        <EnvironmentOutlined style={{ color: '#1890ff' }} />
                                        <span>{typeof regName === 'string' ? regName : regName.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <Empty description="Aucune région enregistrée pour ce délégué" />
                    )}
                </div>
            </Modal>

            {/* --- MODALE DÉTAIL DES MÉDECINS (MANQUÉS / HORS PORTEFEUILLE) --- */}
           <Modal
                title={<Title level={4} style={{ margin: 0 }}>{modalDetails.title}</Title>}
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setDetailModalVisible(false)}>
                        Fermer
                    </Button>
                ]}
                width={850}
            >
                {/* BANDEAU : CYCLE EN COURS ET SES SEMAINES */}
                {modalDetails.currentCycleInfo && (
                    <div style={{ marginBottom: 15, padding: '12px 16px', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: '8px' }}>
                        <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Text strong style={{ color: '#820014', fontSize: '13px' }}>
                                📅 Cycle en cours ({modalDetails.delegateName}) :
                            </Text>
                            <Tag color="volcano" style={{ fontWeight: 'bold', fontSize: '13px' }}>
                                {modalDetails.currentCycleInfo.cycleName}
                            </Tag>
                        </div>

                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                            {modalDetails.currentCycleInfo.weeks?.map((w, idx) => (
                                <Tag 
                                    key={idx}
                                    color={w.isCurrent ? "error" : "default"}
                                    style={{ 
                                        padding: '4px 10px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: w.isCurrent ? 'bold' : 'normal',
                                        border: w.isCurrent ? '2px solid #cf1322' : '1px solid #d9d9d9',
                                        background: w.isCurrent ? '#ffccc7' : '#fafafa'
                                    }}
                                >
                                    <b>Semaine {w.weekNumber}</b> ({w.startShort} au {w.endShort})
                                    {w.isCurrent ? ' 🔴 (Semaine de la visite)' : ''}
                                </Tag>
                            ))}
                        </div>
                    </div>
                )}
                <List
                    itemLayout="horizontal"
                    dataSource={modalDetails.list}
                    locale={{ emptyText: "Aucun médecin dans cette liste." }}
                    renderItem={(item) => (
                        <List.Item
                            actions={[
                                <Button 
                                    size="small" 
                                    icon={<AuditOutlined />} 
                                    onClick={() => handleOpenDoctorHistory(item.id)}
                                >
                                    Parcours
                                </Button>
                            ]}
                        >
                            <List.Item.Meta
                                avatar={
                                    <Avatar 
                                        icon={<UserOutlined />} 
                                        style={{ 
                                            backgroundColor: modalDetails.type === 'missed' ? '#fff1f0' : '#fffbe6', 
                                            color: modalDetails.type === 'missed' ? '#f5222d' : '#faad14' 
                                        }} 
                                    />
                                }
                                title={
                                    <Space>
                                        <Text strong style={{ fontSize: '14px' }}>{item.name}</Text>
                                        {item.isPartner && <Tag color="gold"><StarFilled /> PARTENAIRE</Tag>}
                                    </Space>
                                }
                                description={
                                    <Space direction="vertical" size={4} style={{ width: '100%', marginTop: 4 }}>
                                        <Space wrap>
                                            <Tag color="blue">{item.specialty}</Tag>
                                            <Tag color="cyan"><EnvironmentOutlined /> {item.region}</Tag>
                                            {item.visitDate && (
                                                <Tag color="geekblue" icon={<CalendarOutlined />}>
                                                    Visité le : {item.visitDate}
                                                </Tag>
                                            )}
                                        </Space>

                                        {/* 1. JUSTIFICATION SI VISITE MANQUÉE */}
                                        {/* {modalDetails.type === 'missed' && (
                                            <div style={{ marginTop: 8 }}>
                                                {item.justification ? (
                                                    <div style={{ 
                                                        background: '#fff2f0', 
                                                        border: '1px solid #ffa39e', 
                                                        borderRadius: '6px', 
                                                        padding: '8px 12px',
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: '8px'
                                                    }}>
                                                        <MessageOutlined style={{ color: '#f5222d', fontSize: '16px', marginTop: 2 }} />
                                                        <div style={{ flex: 1 }}>
                                                            <Text strong style={{ color: '#cf1322', fontSize: '12px', display: 'block' }}>
                                                                Raison notée par le délégué :
                                                            </Text>
                                                            <Text italic style={{ color: '#262626', fontSize: '13px', display: 'block', marginTop: 2 }}>
                                                                « {item.justification} »
                                                            </Text>
                                                            {item.justificationDate && (
                                                                <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: 3 }}>
                                                                    Saisie le : {item.justificationDate}
                                                                </Text>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                   <Tag color="default" style={{ color: '#8c8c8c', fontSize: '11px', padding: '2px 8px' }}>
                                                       ⚠️ Aucune raison notée par le délégué pour cette visite
                                                    </Tag>
                                                )}
                                            </div>
                                        )}*/}

                                        {/* 2. DANS QUEL CYCLE ET SEMAINE EST-IL RÉELLEMENT PLANIFIÉ (HORS PLANNING) */}
                                        {modalDetails.type === 'unassigned' && (
                                            <div style={{ marginTop: 6, marginBottom: 6 }}>
                                                <Text strong style={{ fontSize: '12px', color: '#595959' }}>
                                                    Planification dans {modalDetails.currentCycleInfo?.cycleName ? `le ${modalDetails.currentCycleInfo.cycleName}` : 'ce cycle'} :{' '}
                                                </Text>
                                                {item.currentCycleSchedule && item.currentCycleSchedule.length > 0 ? (
                                                    <Space wrap size={[4, 4]}>
                                                        {item.currentCycleSchedule.map((s, idx) => (
                                                            <Tag 
                                                                key={idx} 
                                                                color="volcano" 
                                                                style={{ fontWeight: 'bold', fontSize: '12px', border: '1px solid #ff7a45' }}
                                                            >
                                                                ⚠️ Prévu en Semaine {s.weekNumber} (du {s.startDate} au {s.endDate})
                                                            </Tag>
                                                        ))}
                                                    </Space>
                                                ) : (
                                                    <Tag color="orange" style={{ fontSize: '11px', fontWeight: 'bold' }}>
                                                        ❌ Non planifié dans ce cycle (Visite 100% hors planning)
                                                    </Tag>
                                                )}
                                            </div>
                                        )}

                                        {/* ==================================================================== */}
                                        {/* 2. L'ACCORDÉON DES 13 CYCLES DE L'ANNÉE TRIÉS PAR ORDRE CROISSANT     */}
                                        {/* ==================================================================== */}
                                        {modalDetails.type === 'unassigned' && (
                                            <div style={{ marginTop: 6 }}>
                                                {item.plannedSchedules && item.plannedSchedules.length > 0 ? (
                                                    (() => {
                                                        const sortedSchedules = [...item.plannedSchedules].sort((a, b) => {
                                                            const numA = parseInt(String(a.cycleName).replace(/\D/g, ''), 10) || 0;
                                                            const numB = parseInt(String(b.cycleName).replace(/\D/g, ''), 10) || 0;
                                                            if (numA !== numB) return numA - numB;
                                                            return (a.rawStart || 0) - (b.rawStart || 0);
                                                        });

                                                        return (
                                                            <Collapse
                                                                ghost
                                                                size="small"
                                                                style={{ 
                                                                    background: '#fffbf0', 
                                                                    border: '1px solid #ffd591', 
                                                                    borderRadius: '6px' 
                                                                }}
                                                                items={[
                                                                    {
                                                                        key: 'schedules_list',
                                                                        label: (
                                                                            <Space>
                                                                                <CalendarOutlined style={{ color: '#d46b08' }} />
                                                                                <Text strong style={{ color: '#d46b08', fontSize: '12px' }}>
                                                                                    Planification prévue pour ce délégué (toute l'année)
                                                                                </Text>
                                                                                <Badge 
                                                                                    count={sortedSchedules.length} 
                                                                                    style={{ backgroundColor: '#fa8c16' }} 
                                                                                />
                                                                            </Space>
                                                                        ),
                                                                        children: (
                                                                            <div style={{ 
                                                                                display: 'grid', 
                                                                                gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', 
                                                                                gap: '6px',
                                                                                maxHeight: '180px',
                                                                                overflowY: 'auto',
                                                                                padding: '4px'
                                                                            }}>
                                                                                {sortedSchedules.map((s, idx) => (
                                                                                    <Tag 
                                                                                        key={idx} 
                                                                                        color="volcano" 
                                                                                        style={{ 
                                                                                            padding: '4px 8px', 
                                                                                            margin: 0, 
                                                                                            fontSize: '11px', 
                                                                                            display: 'flex',
                                                                                            justifyContent: 'space-between',
                                                                                            alignItems: 'center',
                                                                                            border: '1px solid #ffbb96'
                                                                                        }}
                                                                                    >
                                                                                        <span>📅 <b>{s.cycleName.toUpperCase()}</b> - S{s.weekNumber}</span>
                                                                                        <span style={{ fontSize: '10px', color: '#595959', marginLeft: 8 }}>
                                                                                            ({s.startDate} au {s.endDate})
                                                                                        </span>
                                                                                    </Tag>
                                                                                ))}
                                                                            </div>
                                                                        )
                                                                    }
                                                                ]}
                                                            />
                                                        );
                                                    })()
                                                ) : (
                                                    <Tag color="orange" style={{ fontSize: '11px' }}>
                                                        ❌ Non planifié dans aucun cycle de ce délégué
                                                    </Tag>
                                                )}
                                            </div>
                                        )}

                                        {/* 3. DÉLÉGUÉ OFFICIEL SI NON ASSIGNÉ AU PORTEFEUILLE */}
                                        {modalDetails.type === 'unassigned' && item.officialDelegates && item.officialDelegates.length > 0 && (
                                            <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: 2 }}>
                                                Assigné au portefeuille de : <b style={{ color: '#1890ff' }}>{item.officialDelegates.join(', ')}</b>
                                            </Text>
                                        )}
                                    </Space>
                                }
                            />
                        </List.Item>
                    )}
                />
            </Modal>

            {/* --- MODALE DÉTAIL DES MÉDECINS VISITÉS HORS PLANNING --- */}
            <Modal
                title={
                    <Space>
                        <EnvironmentOutlined style={{ color: '#f5222d', fontSize: '20px' }} />
                        <Title level={4} style={{ margin: 0 }}>
                            Médecins visités hors planning à <span style={{ color: '#f5222d' }}>{selectedOffPlanInfo.region.toUpperCase()}</span>
                        </Title>
                    </Space>
                }
                open={offPlanModalVisible}
                onCancel={() => setOffPlanModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setOffPlanModalVisible(false)}>
                        Fermer
                    </Button>
                ]}
                width={850}
            >
                {/* 1. EN-TÊTE : DÉLÉGUÉ + CADRE ROUGE DU CYCLE EN COURS ET SES SEMAINES */}
                <div style={{ marginBottom: 15, padding: '12px 16px', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: '8px' }}>
                    <Row gutter={[16, 8]} align="middle">
                        <Col span={12}>
                            <Text strong>Délégué : </Text>
                            <Text style={{ color: '#cf1322', fontWeight: 'bold', fontSize: '14px' }}>{selectedOffPlanInfo.delegateName}</Text>
                        </Col>
                        <Col span={12} style={{ textAlign: 'right' }}>
                            <Text type="secondary">
                                Total : <b style={{ color: '#cf1322' }}>{selectedOffPlanInfo.doctors.length}</b> médecin(s) visité(s) hors planning
                            </Text>
                        </Col>

                        {/* --- ZONE DU CADRE ROUGE : CYCLE EN COURS & TOUTES SES SEMAINES --- */}
                        <Col span={24} style={{ marginTop: 6, paddingTop: 8, borderTop: '1px dashed #ffa39e' }}>
                            {selectedOffPlanInfo.currentCycleInfo ? (
                                <div>
                                    <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Text strong style={{ color: '#820014', fontSize: '13px' }}>
                                            📅 Cycle en cours :
                                        </Text>
                                        <Tag color="volcano" style={{ fontWeight: 'bold', fontSize: '13px' }}>
                                            {selectedOffPlanInfo.currentCycleInfo.cycleName}
                                        </Tag>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                                        {/* Semaines normales (non actives) */}
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            {selectedOffPlanInfo.currentCycleInfo.weeks?.filter(w => !w.isCurrent).map((w, idx) => (
                                                <Tag 
                                                    key={idx}
                                                    style={{ 
                                                        padding: '3px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        border: '1px solid #d9d9d9',
                                                        background: '#fafafa',
                                                        color: '#595959'
                                                    }}
                                                >
                                                    Semaine {w.weekNumber} ({w.startShort} au {w.endShort})
                                                </Tag>
                                            ))}
                                        </div>

                                        {/* SEMAINE ACTIVE + CADRE ROUGE DES RÉGIONS PRÉVUES */}
                                        {selectedOffPlanInfo.currentCycleInfo.weeks?.filter(w => w.isCurrent).map((w, idx) => (
                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                {/* Tag de la semaine de la visite */}
                                                <Tag 
                                                    color="error"
                                                    style={{ 
                                                        padding: '4px 10px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        fontWeight: 'bold',
                                                        border: '2px solid #cf1322',
                                                        background: '#ffccc7',
                                                        margin: 0
                                                    }}
                                                >
                                                    Semaine {w.weekNumber} ({w.startShort} au {w.endShort}) 🔴 (Semaine de la visite)
                                                </Tag>

                                                {/* --- EXACTEMENT DANS LE RECTANGLE ROUGE : RÉGIONS PRÉVUES --- */}
                                                <div style={{ 
                                                    display: 'inline-flex', 
                                                    alignItems: 'center', 
                                                    gap: 6, 
                                                    background: '#ffffff', 
                                                    padding: '3px 10px', 
                                                    borderRadius: '4px',
                                                    border: '1px solid #91caff'
                                                }}>
                                                    <Text strong style={{ color: '#003a8c', fontSize: '11px' }}>
                                                        📍 Régions prévues cette semaine :
                                                    </Text>
                                                    {selectedOffPlanInfo.currentCycleInfo.plannedRegionsForCurrentWeek && selectedOffPlanInfo.currentCycleInfo.plannedRegionsForCurrentWeek.length > 0 ? (
                                                        selectedOffPlanInfo.currentCycleInfo.plannedRegionsForCurrentWeek.map((reg, rIdx) => (
                                                            <Tag key={rIdx} color="blue" style={{ fontWeight: 'bold', fontSize: '11px', margin: 0 }}>
                                                                {reg}
                                                            </Tag>
                                                        ))
                                                    ) : (
                                                        <Text type="secondary" style={{ fontSize: '11px' }}>Aucune région définie</Text>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <Tag color="orange">Aucun cycle actif trouvé pour ce délégué à cette date.</Tag>
                            )}
                        </Col>
                    </Row>
                </div>

                {/* 2. LISTE DES MÉDECINS AVEC LEUR POSITION DANS LE CYCLE EN COURS */}
                <List
                    itemLayout="horizontal"
                    dataSource={selectedOffPlanInfo.doctors}
                    locale={{ emptyText: "Aucun médecin enregistré pour cette région." }}
                    renderItem={(item) => (
                        <List.Item
                            actions={[
                                <Button 
                                    size="small" 
                                    type="primary" 
                                    ghost
                                    icon={<AuditOutlined />} 
                                    onClick={() => handleOpenDoctorHistory(item.id)}
                                >
                                    Parcours
                                </Button>
                            ]}
                        >
                            <List.Item.Meta
                                avatar={
                                    <Avatar 
                                        icon={<UserOutlined />} 
                                        style={{ backgroundColor: '#ffccc7', color: '#f5222d' }} 
                                    />
                                }
                                title={
                                    <Space>
                                        <Text strong style={{ fontSize: '14px' }}>{item.name}</Text>
                                        {item.isPartner && <Tag color="gold"><StarFilled /> PARTENAIRE</Tag>}
                                        <Tag color="blue" icon={<CalendarOutlined />}>
                                            Visité le {item.visitFullDate || `${item.visitDate} à ${item.visitTime}`}
                                        </Tag>
                                    </Space>
                                }
                                description={
                                    <Space direction="vertical" size={4} style={{ marginTop: 4 }}>
                                        <Space>
                                            <Tag color="blue">{item.specialty}</Tag>
                                            <Tag color="cyan"><EnvironmentOutlined /> {item.region}</Tag>
                                        </Space>

                                        {/* POSITION DU MÉDECIN DANS CE CYCLE EN COURS */}
                                        <div style={{ marginTop: 4 }}>
                                            <Text strong style={{ fontSize: '12px', color: '#595959' }}>
                                                Planification dans ce cycle :{' '}
                                            </Text>
                                            {item.currentCycleSchedule && item.currentCycleSchedule.length > 0 ? (
                                                <Space wrap size={[4, 4]}>
                                                    {item.currentCycleSchedule.map((s, idx) => (
                                                        <Tag 
                                                            key={idx} 
                                                            color={s.isCurrentWeek ? "green" : "volcano"} 
                                                            style={{ fontWeight: 'bold', fontSize: '12px' }}
                                                        >
                                                            ⚠️ Prévu en Semaine {s.weekNumber} (du {s.startDate} au {s.endDate})
                                                        </Tag>
                                                    ))}
                                                </Space>
                                            ) : (
                                                <Tag color="orange" style={{ fontSize: '11px' }}>
                                                    ❌ Non planifié dans ce cycle (Visite 100% hors planning)
                                                </Tag>
                                            )}
                                        </div>
                                    </Space>
                                }
                            />
                        </List.Item>
                    )}
                />
            </Modal>



            {/* --- MODALE HISTORIQUE DES SEMAINES PRÉCÉDENTES --- */}
            <Modal
                title={
                    <Space>
                        <HistoryOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                        <Title level={4} style={{ margin: 0 }}>
                            Historique des Semaines : Audit & Contrôle Hebdomadaire
                        </Title>
                    </Space>
                }
                open={prevWeekHistoryModalVisible}
                onCancel={() => setPrevWeekHistoryModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setPrevWeekHistoryModalVisible(false)}>
                        Fermer
                    </Button>
                ]}
                width={1050}
            >
                {/* BARRE DE CHOIX DE LA SEMAINE */}
                <div style={{ marginBottom: 20, padding: '12px 16px', background: '#f5f7fa', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                    <Space align="center" wrap>
                        <Text strong>Choisir la semaine à auditer :</Text>
                        <RangePicker 
                            value={historyWeekRange} 
                            onChange={(dates) => {
                                setHistoryWeekRange(dates);
                                fetchHistoryWeekCompliance(dates);
                            }} 
                            format="DD/MM/YYYY"
                            allowClear={false}
                        />
                        <Button type="primary" icon={<ReloadOutlined />} onClick={() => fetchHistoryWeekCompliance(historyWeekRange)}>
                            Charger
                        </Button>
                    </Space>

                    {historyWeekData?.periodLabel && (
                        <Tag color="blue" style={{ fontSize: '12px', padding: '4px 10px', fontWeight: 'bold' }}>
                            📅 Période : {historyWeekData.periodLabel}
                        </Tag>
                    )}
                </div>

                {/* TABLEAU DES RÉSULTATS POUR LA SEMAINE CHOISIE */}
                <Table 
                    dataSource={historyWeekData?.delegates || []}
                    columns={prevWeekColumns}
                    rowKey="delegateId"
                    loading={loadingHistoryWeek}
                    pagination={false}
                    size="middle"
                    scroll={{ y: 400 }}
                    locale={{ emptyText: "Aucune donnée disponible pour cette semaine." }}
                />
            </Modal>

            <Modal
                title={
                    <Space>
                        <CrownOutlined style={{ color: '#722ed1', fontSize: '20px' }} />
                        <Title level={4} style={{ margin: 0 }}>
                            Visites de Contrôle & Accompagnement : <span style={{ color: '#722ed1' }}>{selectedSupervisorVisits.delegateName}</span>
                        </Title>
                    </Space>
                }
                open={supervisorModalVisible}
                onCancel={() => setSupervisorModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setSupervisorModalVisible(false)}>
                        Fermer
                    </Button>
                ]}
                width={800}
            >
                <List
                    itemLayout="vertical"
                    dataSource={selectedSupervisorVisits.visits}
                    locale={{ emptyText: "Aucune visite de supervision trouvée." }}
                    renderItem={(visit, idx) => {
                        const isCreation = (visit.controlType || '').toUpperCase().includes('CREATION') || (visit.controlType || '').toUpperCase().includes('CRÉATION');

                        return (
                            <List.Item
                                key={idx}
                                style={{ 
                                    background: '#fafafa', 
                                    border: '1px solid #d3adf7', 
                                    borderRadius: '8px', 
                                    marginBottom: 12, 
                                    padding: '14px 18px' 
                                }}
                            >
                                <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                                    <Col>
                                        <Space wrap>
                                            <MedicineBoxOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                                            <Text strong style={{ fontSize: '15px' }}>{visit.doctorName}</Text>
                                            <Tag color="cyan">{visit.region}</Tag>
                                            <Tag color="blue">{visit.specialty}</Tag>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            {visit.rating && <Tag color="gold" style={{ fontWeight: 'bold' }}>⭐ {visit.rating}</Tag>}
                                            <Tag color={isCreation ? "green" : "purple"} style={{ fontWeight: 'bold' }}>
                                                {isCreation ? "✨ Création" : (visit.controlType || 'Accompagnement')}
                                            </Tag>
                                        </Space>
                                    </Col>
                                </Row>

                                <div style={{ marginBottom: 8, fontSize: '12px', color: '#595959' }}>
                                    📅 <b>Date :</b> {visit.date} | 👑 <b>Superviseur :</b> <span style={{ color: '#722ed1', fontWeight: 'bold' }}>{visit.supervisorName}</span>
                                    {visit.delegateName && (
                                        <>
                                            {' | '} 👤 <b>Délégué(s) accompagné(s) :</b> <span style={{ color: '#096dd9', fontWeight: 'bold' }}>{visit.delegateName}</span>
                                        </>
                                    )}
                                </div>

                                {/* ⚡ ENCADRÉ POTENTIEL MÉDECIN & PRODUIT SI CRÉATION ⚡ */}
                                {(visit.doctorPotential || visit.productPotential) && (
                                    <div style={{ 
                                        background: '#f6ffed', 
                                        border: '1px solid #b7eb8f', 
                                        borderRadius: 6, 
                                        padding: '10px 14px', 
                                        marginBottom: 10,
                                        marginTop: 6
                                    }}>
                                        <Text strong style={{ color: '#389e0d', display: 'block', marginBottom: 4, fontSize: '12px' }}>
                                            🎯 Qualification de la Création :
                                        </Text>
                                        <Space size={20} wrap>
                                            {visit.doctorPotential && (
                                                <span>
                                                    <strong>Potentiel Médecin :</strong>{' '}
                                                    <Tag color="green" style={{ fontWeight: 'bold', fontSize: '12px' }}>
                                                        {visit.doctorPotential}
                                                    </Tag>
                                                </span>
                                            )}
                                            {visit.productPotential && (
                                                <span>
                                                    <strong>Potentiel Produit :</strong>{' '}
                                                    <Tag color="cyan" style={{ fontWeight: 'bold', fontSize: '12px' }}>
                                                        {visit.productPotential}
                                                    </Tag>
                                                </span>
                                            )}
                                        </Space>
                                    </div>
                                )}

                                {visit.comment && (
                                    <div style={{ 
                                        background: '#ffffff', 
                                        padding: '10px 14px', 
                                        borderRadius: '6px', 
                                        border: '1px dashed #d3adf7',
                                        marginTop: 6
                                    }}>
                                        <Text strong style={{ color: '#722ed1', display: 'block', marginBottom: 4 }}>
                                            📝 Observations du Superviseur :
                                        </Text>
                                        <Text italic style={{ color: '#262626' }}>
                                            « {visit.comment} »
                                        </Text>
                                    </div>
                                )}
                            </List.Item>
                        );
                    }}
                />
            </Modal>

        </div>
    );
};

export default DashboardPage;